import { DATE_VALUE_FORMAT } from "components/DatePicker/format"
import { DateSpanInterval, FilterSelection, QueryFilter, TimeComparisonInterval } from "gen/graphql"
import { DateTime } from "luxon"
import { QueryParamConfig } from "use-query-params"

export const ComparedTimeComparisonParam: QueryParamConfig<TimeComparisonInterval | undefined> = {
  encode: (value) => (value ? String(value) : undefined),
  decode: (value) => {
    return isComparisonKey(value) ? value : undefined
  },
}

export type CustomTimespan = `${string},${string}`
export type TimespanKey = DateSpanInterval | CustomTimespan

export const TimespanKeyWithDefaultParam = (
  defaultValue: TimespanKey,
): QueryParamConfig<TimespanKey> => {
  return {
    encode: (value) => (value ? String(value) : undefined),
    decode: (value) => {
      if (isTimespanKey(value)) {
        return value
      }
      return defaultValue
    },
    default: defaultValue,
  }
}

export const TimespanKeyParam: QueryParamConfig<TimespanKey | undefined> = {
  encode: (value) => (value ? String(value) : undefined),
  decode: (value) => {
    if (isTimespanKey(value)) {
      return value
    }
    return undefined
  },
}

export function isCustomTimespan(
  value: string | (string | null)[] | null | undefined,
): value is CustomTimespan {
  if (typeof value !== "string") {
    return false
  }
  const dates = value.split(",")
  if (dates.length !== 2) {
    return false
  }
  const first = DateTime.fromFormat(dates[0], DATE_VALUE_FORMAT)
  const second = DateTime.fromFormat(dates[1], DATE_VALUE_FORMAT)
  if (!first.isValid || !second.isValid) {
    return false
  }
  return true
}

function isComparisonKey(
  value: string | (string | null)[] | null | undefined,
): value is TimeComparisonInterval {
  if (typeof value !== "string") {
    return false
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (
    value === TimeComparisonInterval.LastYear || value === TimeComparisonInterval.PreviousPeriod
  )
}

function isTimespanKey(value: string | (string | null)[] | null | undefined): value is TimespanKey {
  if (typeof value !== "string") {
    return false
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isRelative = Object.values(DateSpanInterval).includes(value as any)
  if (isRelative) {
    return true
  }
  return isCustomTimespan(value)
}

export function StringWithDefaultParam<T>(defaultValue: T): QueryParamConfig<T> {
  return {
    encode: (value) => (value ? String(value) : undefined),
    decode: (value) => (value || defaultValue) as T,
    default: defaultValue,
  }
}

export const GroupingParam: QueryParamConfig<string | undefined> = {
  encode: (value) => value,
  decode: (value) => (typeof value === "string" ? value : undefined),
  default: undefined,
}

export const FiltersParam: QueryParamConfig<QueryFilter[]> = {
  encode: (value) => encodeURIComponent(JSON.stringify(value)),
  decode: (value) =>
    typeof value === "string" && value ? JSON.parse(decodeURIComponent(value)) : [],
  default: [],
}

export const FilterSelectionParam: QueryParamConfig<FilterSelection[]> = {
  encode: (value) => encodeURIComponent(JSON.stringify(value)),
  decode: (value) =>
    typeof value === "string" && value ? JSON.parse(decodeURIComponent(value)) : [],
  default: [],
}

export const DateTimeParam: QueryParamConfig<DateTime> = {
  encode: (value) => value.toISODate(),
  decode: (value) =>
    typeof value === "string" && value ? DateTime.fromISO(value) : DateTime.now(),
  default: DateTime.now(),
}
